/**
 * useVoiceSession Hook
 * 
 * React hook for managing OpenAI Realtime API voice sessions.
 * Provides voice chat functionality with state management.
 * 
 * @example
 * ```tsx
 * const { connect, disconnect, state, isSupported } = useVoiceSession({
 *   language: 'en',
 *   onTranscript: (text) => console.log('User said:', text),
 *   onAIResponse: (text) => console.log('AI said:', text),
 * });
 * ```
 * 
 * @author Heseda AI Team
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  VoiceSession,
  VoiceSessionState,
  VoiceSessionConfig,
  isVoiceSupported,
  buildVoiceSystemPrompt,
  VoiceSearchContext,
} from '../services/voiceService';
import type { MasterProfile } from '../types';
import { trackPageView } from '../services/activityTrackingService';

export interface UseVoiceSessionOptions {
  /** Language for voice session */
  language: string;
  /** Master profile for context */
  masterProfile?: Partial<MasterProfile>;
  /** Profile completion percentage */
  profileCompletion?: number;
  /** Search context from Firebase for consistent behavior with text AI */
  searchContext?: VoiceSearchContext;
  /** Called when user starts speaking (speech detected) */
  onSpeechStart?: () => void;
  /** Called when user's speech is transcribed */
  onTranscript?: (text: string, isFinal: boolean) => void;
  /** Called when AI responds */
  onAIResponse?: (text: string, isFinal: boolean) => void;
  /** Called on error */
  onError?: (error: string) => void;
  /** Called when state changes */
  onStateChange?: (state: VoiceSessionState) => void;
  /** Called when session auto-closes due to inactivity (60s timeout) */
  onInactivityTimeout?: () => void;
  /** Custom system prompt override */
  systemPromptOverride?: string;
}

export interface UseVoiceSessionReturn {
  /** Connect and start voice session */
  connect: () => Promise<void>;
  /** Disconnect voice session */
  disconnect: () => void;
  /** Toggle voice session (connect if disconnected, disconnect if connected) */
  toggle: () => Promise<void>;
  /** Send a text message in voice mode */
  sendText: (text: string) => void;
  /** Update AI context with search results for position-based queries */
  updateSearchContext: (searchResults: Array<{
    name: string;
    link?: string;
    price?: string | number;
    reason?: string;
  }>) => void;
  /** Current session state */
  state: VoiceSessionState;
  /** Whether voice is supported in current browser */
  isSupported: boolean;
  /** Whether currently connected */
  isConnected: boolean;
  /** Whether currently listening to user */
  isListening: boolean;
  /** Whether AI is currently speaking */
  isSpeaking: boolean;
  /** Error message if any */
  error: string | null;
}

/**
 * Hook for managing voice chat sessions with OpenAI Realtime API
 */
export function useVoiceSession(options: UseVoiceSessionOptions): UseVoiceSessionReturn {
  const { 
    language, 
    masterProfile, 
    profileCompletion = 0,
  } = options;
  
  const sessionRef = useRef<VoiceSession | null>(null);
  
  const [state, setState] = useState<VoiceSessionState>({
    status: 'disconnected',
  });

  const [isSupported] = useState(() => isVoiceSupported());

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        sessionRef.current.disconnect();
        sessionRef.current = null;
      }
    };
  }, []);

  /**
   * Connect to voice session
   */
  const connect = useCallback(async () => {
    if (!isSupported) {
      const error = 'Voice not supported in this browser';
      setState({ status: 'error', error });
      options.onError?.(error);
      return;
    }

    // Disconnect existing session if any
    if (sessionRef.current) {
      sessionRef.current.disconnect();
    }

    // Build system prompt with search context (mirrors text AI behavior)
    const systemPrompt = options.systemPromptOverride || buildVoiceSystemPrompt(
      language,
      masterProfile,
      profileCompletion,
      options.searchContext // Pass search context for consistent text/voice behavior
    );

    const config: VoiceSessionConfig = {
      language,
      systemPrompt,
      // shimmer is good for Korean (natural, softer tone), alloy for English
      voice: language === 'ko' ? 'shimmer' : 'alloy',
    };

    const session = new VoiceSession(config, {
      onStateChange: (newState) => {
        setState(newState);
        options.onStateChange?.(newState);
      },
      onSpeechStart: () => {
        options.onSpeechStart?.();
      },
      onTranscript: (text, isFinal) => {
        options.onTranscript?.(text, isFinal);
      },
      onAIResponse: (text, isFinal) => {
        options.onAIResponse?.(text, isFinal);
      },
      onError: (error) => {
        options.onError?.(error);
      },
      onInactivityTimeout: () => {
        options.onInactivityTimeout?.();
      },
    });

    sessionRef.current = session;

    try {
      await session.connect();
    } catch (error) {
      console.error('Failed to connect voice session:', error);
    }
  }, [isSupported, language, masterProfile, profileCompletion, options]);

  /**
   * Disconnect voice session
   */
  const disconnect = useCallback(() => {
    if (sessionRef.current) {
      sessionRef.current.disconnect();
      sessionRef.current = null;
    }
    setState({ status: 'disconnected' });
  }, []);

  /**
   * Toggle voice session
   */
  const toggle = useCallback(async () => {
    if (sessionRef.current?.isConnected()) {
      disconnect();
    } else {

      // User Activity
      trackPageView('voice-chat');
      
      await connect();
    }
  }, [connect, disconnect]);

  /**
   * Send text message in voice mode
   */
  const sendText = useCallback((text: string) => {
    if (sessionRef.current?.isConnected()) {
      sessionRef.current.sendTextMessage(text);
    }
  }, []);

  /**
   * Update AI context with search results
   * Call this after search completes so AI can reference products by position
   */
  const updateSearchContext = useCallback((searchResults: Array<{
    name: string;
    link?: string;
    price?: string | number;
    reason?: string;
  }>) => {
    if (sessionRef.current?.isConnected()) {
      sessionRef.current.updateSearchContext(searchResults);
    }
  }, []);

  return {
    connect,
    disconnect,
    toggle,
    sendText,
    updateSearchContext,
    state,
    isSupported,
    isConnected: state.status === 'connected' || state.status === 'listening' || state.status === 'speaking',
    isListening: state.status === 'listening',
    isSpeaking: state.status === 'speaking',
    error: state.error || null,
  };
}
